import React, { useState, useEffect } from 'react';
import LORManagerSelector from './LORManagerSelector';
import Icon from '../../components/Icon';

const MOVE_ICONS = { up: '↑', down: '↓', flat: '→' };
const FLAG_CLASSES = { bye: 'bye', out: 'out', doubtful: 'out', questionable: 'q' };

export default function LORCommandCenter({ api, status }) {
  const [standings, setStandings]     = useState([]);
  const [rankings, setRankings]       = useState([]);
  const [rosterId, setRosterId]       = useState(null);
  const [intelData, setIntelData]     = useState(null);
  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingIntel, setLoadingIntel] = useState(false);

  const isPreSeason = standings.every(s => s.wins === 0 && s.losses === 0);

  useEffect(() => {
    Promise.all([
      fetch(`${api}/api/lor/standings`).then(r => r.json()).catch(() => []),
      fetch(`${api}/api/lor/power-rankings`).then(r => r.json()).catch(() => []),
    ]).then(([s, r]) => {
      setStandings(s || []);
      setRankings(r || []);
      setLoadingBase(false);
    });
  }, [api]);

  useEffect(() => {
    if (!rosterId || isPreSeason) { setIntelData(null); setLoadingIntel(false); return; }
    setLoadingIntel(true);
    fetch(`${api}/api/lor/intel/weekly/${rosterId}`)
      .then(r => r.json())
      .catch(() => null)
      .then(d => { setIntelData(d); setLoadingIntel(false); });
  }, [api, rosterId, isPreSeason]);

  return (
    <div className="lor-command-center">
      {/* Hero */}
      <div className="lor-cc-hero">
        <div className="lor-cc-hero-icon"><Icon emoji="🏆" size={28} /></div>
        <div>
          <h2 className="lor-cc-hero-title">League of Record</h2>
          <div className="lor-cc-hero-sub">
            {status?.league_name || 'League of Record'} · Est. 2012 ·{' '}
            {isPreSeason
              ? 'Pre-Draft'
              : `Season ${status?.season || '2026'} · Week ${status?.current_week || 1}`
            }
          </div>
        </div>
      </div>

      {isPreSeason && (
        <div className="lor-preseason-banner">
          <Icon emoji="⚡" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> <strong>2026 Season</strong> — Draft not yet started. Standings and power rankings will populate once play begins.
        </div>
      )}

      {/* Manager selector */}
      <div className="lor-cc-manager-row">
        <LORManagerSelector api={api} onSelect={setRosterId} selectedRosterId={rosterId} />
        {rosterId && loadingIntel && (
          <span className="lor-cc-intel-loading">Loading your intel…</span>
        )}
      </div>

      {/* Personal intel panel (when manager selected + season active) */}
      {!isPreSeason && intelData && !loadingIntel && (
        <PersonalIntelPanel data={intelData} />
      )}

      {/* League overview grid */}
      <div className="lor-cc-grid">
        {/* Standings preview */}
        <div className="lor-card lor-cc-card">
          <div className="lor-card-title">Standings</div>
          {loadingBase ? (
            <div className="lor-loading">Loading…</div>
          ) : standings.length === 0 ? (
            <div className="lor-empty">No standings yet — season hasn't started.</div>
          ) : (
            <table className="lor-standings-table lor-standings-table--compact">
              <thead>
                <tr>
                  <th>RK</th><th>Manager</th><th>W</th><th>L</th><th>PF</th><th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {standings.slice(0, 5).map(s => (
                  <tr key={s.roster_id} className={s.rank <= 4 ? 'lor-playoff-spot' : ''}>
                    <td className="lor-rank">{s.rank}</td>
                    <td className="lor-manager-name">
                      {s.avatar && (
                        <img
                          src={`https://sleepercdn.com/avatars/thumbs/${s.avatar}`}
                          alt={s.display_name}
                          className="lor-avatar"
                        />
                      )}
                      {s.display_name}
                    </td>
                    <td className="lor-stat">{s.wins}</td>
                    <td className="lor-stat">{s.losses}</td>
                    <td className="lor-stat">{s.pf}</td>
                    <td className={`lor-streak lor-streak--${s.streak?.type?.toLowerCase()}`}>
                      {s.streak?.count > 0 ? `${s.streak.type}${s.streak.count}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Power rankings preview */}
        <div className="lor-card lor-cc-card">
          <div className="lor-card-title">Power Rankings</div>
          {loadingBase ? (
            <div className="lor-loading">Loading…</div>
          ) : rankings.length === 0 ? (
            <div className="lor-empty">Power rankings will appear once rosters are set.</div>
          ) : (
            <div className="lor-pr-list">
              {rankings.slice(0, 5).map(r => (
                <div key={r.roster_id} className="lor-pr-row">
                  <span className="lor-pr-rank">#{r.power_rank}</span>
                  <span className="lor-pr-name">{r.display_name}</span>
                  <span className="lor-pr-score">{r.power_score?.toFixed(1)}</span>
                  <span className={`lor-pr-trend ${r.rank_change > 0 ? 'up' : r.rank_change < 0 ? 'down' : 'flat'}`}>
                    {r.rank_change > 0 ? `↑${r.rank_change}` : r.rank_change < 0 ? `↓${Math.abs(r.rank_change)}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* League info */}
        <div className="lor-card lor-cc-card lor-cc-info">
          <div className="lor-card-title">League Settings</div>
          <div className="lor-info-grid">
            <div className="lor-info-item">
              <span className="lor-info-label">Format</span>
              <span className="lor-info-value">Full PPR · SuperFlex</span>
            </div>
            <div className="lor-info-item">
              <span className="lor-info-label">Keepers</span>
              <span className="lor-info-value">2 per team</span>
            </div>
            <div className="lor-info-item">
              <span className="lor-info-label">Playoffs</span>
              <span className="lor-info-value">4 teams · Week 16</span>
            </div>
            <div className="lor-info-item">
              <span className="lor-info-label">Waivers</span>
              <span className="lor-info-value">FAAB $100</span>
            </div>
            <div className="lor-info-item">
              <span className="lor-info-label">Trade Deadline</span>
              <span className="lor-info-value">Week 11</span>
            </div>
            <div className="lor-info-item">
              <span className="lor-info-label">Teams</span>
              <span className="lor-info-value">10</span>
            </div>
          </div>
        </div>

        {/* History teaser */}
        <div className="lor-card lor-cc-card lor-cc-history">
          <div className="lor-card-title">Championship History</div>
          <div className="lor-history-teaser">
            <div className="lor-history-teaser-icon">📜</div>
            <p>
              The League of Record has run since <strong>2012</strong>. Championship history is
              being built into the archive now.
            </p>
            <a href="/league-of-record/history" className="lor-history-link">
              View History →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonalIntelPanel({ data }) {
  const { key_action, matchup, playoff_race, top_waiver_target, bye_alert, lineup } = data;

  // Count flags in lineup
  const flaggedCount = (lineup || []).filter(p => p.flag).length;

  return (
    <div className="lor-cc-intel-panel">
      <div className="lor-cc-intel-header">
        <Icon emoji="⚡" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Your Week {data.current_week} Intel
        <span className="lor-generated-badge" style={{ marginLeft: 8 }}>rules-based</span>
      </div>

      <div className="lor-cc-intel-inner">
        {/* Key action item */}
        {key_action && (
          <div className={`lor-cc-action lor-cc-action--${key_action.priority}`}>
            <span className="lor-cc-action-icon">{key_action.icon}</span>
            <div className="lor-cc-action-text">{key_action.text}</div>
          </div>
        )}

        <div className="lor-cc-intel-cards">
          {/* Matchup */}
          {matchup && (
            <div className="lor-cc-intel-mini">
              <div className="lor-cc-intel-mini-label"><Icon emoji="⚔️" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Matchup</div>
              <div className="lor-cc-intel-mini-vs">
                <span className="lor-cc-intel-proj-me">{matchup.my_projected}</span>
                <span className="lor-cc-intel-mini-vstext">vs</span>
                <span className="lor-cc-intel-proj-opp">{matchup.opp_projected}</span>
              </div>
              <div className="lor-cc-intel-mini-sub">vs {matchup.opponent_display_name}</div>
              <div className={matchup.projected_winner === 'me'
                ? 'lor-cc-intel-win' : 'lor-cc-intel-loss'}>
                {matchup.projected_winner === 'me'
                  ? `Projected win +${Math.abs(matchup.projected_margin)}`
                  : `Projected loss −${Math.abs(matchup.projected_margin)}`
                }
              </div>
            </div>
          )}

          {/* Playoff race */}
          {playoff_race && (
            <div className="lor-cc-intel-mini">
              <div className="lor-cc-intel-mini-label"><Icon emoji="🏆" size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Playoff Race</div>
              <div className="lor-cc-intel-playoff-pct">
                {(playoff_race.playoff_pct || 0).toFixed(0)}%
                <span className={`lor-cc-playoff-mv lor-cc-playoff-mv--${playoff_race.movement_dir}`}>
                  {MOVE_ICONS[playoff_race.movement_dir]}
                </span>
              </div>
              <div className="lor-cc-intel-mini-sub">
                #{playoff_race.rank} in standings
                {playoff_race.in_playoff_position
                  ? <span className="lor-cc-in-playoff"> · In playoff spot</span>
                  : <span className="lor-cc-out-playoff"> · Outside cutline</span>
                }
              </div>
            </div>
          )}

          {/* Flags summary */}
          {flaggedCount > 0 && (
            <div className="lor-cc-intel-mini lor-cc-intel-mini--flag">
              <div className="lor-cc-intel-mini-label">⚕️ Lineup Flags</div>
              <div className="lor-cc-intel-flag-count">{flaggedCount}</div>
              <div className="lor-cc-intel-mini-sub">
                {(lineup || [])
                  .filter(p => p.flag)
                  .map(p => `${p.full_name} (${p.flag_label})`)
                  .join(' · ')
                }
              </div>
            </div>
          )}

          {/* Top waiver */}
          {top_waiver_target && (
            <div className="lor-cc-intel-mini">
              <div className="lor-cc-intel-mini-label">🚨 Top Add</div>
              <div className="lor-cc-intel-waiver-name">{top_waiver_target.full_name}</div>
              <div className="lor-cc-intel-mini-sub">
                {top_waiver_target.position} · {top_waiver_target.projected} proj
                {top_waiver_target.faab_suggestion && ` · ${top_waiver_target.faab_suggestion}`}
              </div>
            </div>
          )}

          {/* Bye alert */}
          {bye_alert && (
            <div className={`lor-cc-intel-mini lor-cc-intel-mini--bye lor-cc-intel-mini--${bye_alert.severity}`}>
              <div className="lor-cc-intel-mini-label">🗓️ Bye Alert</div>
              <div className="lor-cc-intel-bye-count">{bye_alert.bye_starters.length} on bye</div>
              <div className="lor-cc-intel-mini-sub">
                {bye_alert.bye_starters.map(p => p.full_name).join(', ')}
              </div>
            </div>
          )}
        </div>

        <a href="/league-of-record/intel" className="lor-cc-intel-link">
          Full Weekly Intel →
        </a>
      </div>
    </div>
  );
}
